/**
 * Multiplayer client. Clock offset: 8 ping samples, keep the minimum-RTT sample, offset = s - (c + rtt/2); re-sampled
 * every 15 s. Round timeline: sim = SIM_BASE + (serverNow - startAt). Server orders all actions; local latency settings
 * do not apply to room tokens (orders are forwarded and settled by the server's fill).
 */
import { create } from 'zustand';
import { Market } from '../../shared/market';
import type { RoomConfig, RoomPublic, ServerMsg, Standing } from '../../shared/protocol';
import type { RoundSpec } from '../../shared/types';
import { useSettings } from '../store/settings';
import { toast } from '../store/toast';
import { endRound, startRound, abortRound, useGame } from './game';
import { exitMp, market, useMpMarket } from './runtime';
import { load, remove, save } from './storage';

const SIM_BASE = 10_000;
export interface ChatItem { from: string; name: string; text: string; at: number }
interface RoomState {
  code: string | null; status: 'idle' | 'connecting' | 'open' | 'closed'; room: RoomPublic | null; pid: string; chat: ChatItem[];
  board: { pid: string; name: string; acted: boolean; pnlSol: number }[]; results: { round: number; standings: Standing[]; totals: { pid: string; name: string; points: number }[]; final: boolean } | null;
  offset: number; rtt: number | null; spectator: boolean; round: number; startAt: number; error: string;
}
export const useRoom = create<RoomState>(() => ({ code: null, status: 'idle', room: null, pid: '', chat: [], board: [], results: null, offset: 0, rtt: null, spectator: false, round: 0, startAt: 0, error: '' }));

let ws: WebSocket | null = null; let retry = 0; let samples: { rtt: number; off: number }[] = []; let pingId = 0; let syncTimer: number | null = null; let closing = false;
const pending = new Map<number, number>();
const serverNow = () => performance.timeOrigin + performance.now() + useRoom.getState().offset;

export function send(m: unknown) { if (ws?.readyState === 1) ws.send(JSON.stringify(m)); }
export async function createRoom(): Promise<string> { const r = await fetch('/api/rooms', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' }); const j = await r.json(); if (!r.ok) throw new Error(j.error); return j.code; }

export function join(code: string) {
  if (useRoom.getState().code === code && ws && ws.readyState <= 1) return;
  leave(false); closing = false;
  useRoom.setState({ code, status: 'connecting', chat: [], results: null, board: [], error: '' });
  open(code);
}
function open(code: string) {
  const seat = load<{ pid: string; key: string } | null>(`seat.${code}`, null);
  const name = useSettings.getState().playerName || 'guest';
  const q = new URLSearchParams({ name, ...(seat ? { pid: seat.pid, key: seat.key } : {}) });
  ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/room/${code}?${q}`);
  ws.onopen = () => { retry = 0; useRoom.setState({ status: 'open' }); samples = []; for (let i = 0; i < 8; i++) setTimeout(ping, i * 120); syncTimer = window.setInterval(() => { samples = samples.slice(-8); ping(); }, 15000); };
  ws.onmessage = (e) => { try { onMsg(JSON.parse(String(e.data)) as ServerMsg); } catch (err) { console.error(err); } };
  ws.onclose = (e) => {
    if (syncTimer) clearInterval(syncTimer);
    useRoom.setState({ status: 'closed' });
    if (closing) return;
    if (e.code === 4004 || e.code === 4003 || e.code === 4001) { useRoom.setState({ error: e.reason || 'closed' }); return; }
    const d = Math.min(15000, 500 * 2 ** retry++); setTimeout(() => { if (!closing && useRoom.getState().code === code) open(code); }, d);
  };
}
function ping() { const id = ++pingId; const c = performance.timeOrigin + performance.now(); pending.set(id, c); send({ t: 'ping', id, c }); }

function onMsg(m: ServerMsg) {
  const st = useRoom.getState();
  switch (m.t) {
    case 'hello': save(`seat.${m.room.code}`, { pid: m.pid, key: m.key }); useRoom.setState({ pid: m.pid, room: m.room, offset: m.serverNow - (performance.timeOrigin + performance.now()) }); break;
    case 'pong': {
      const c = pending.get(m.id); if (c == null) break; pending.delete(m.id);
      const t = performance.timeOrigin + performance.now(); const rtt = t - c; samples.push({ rtt, off: m.s - (c + rtt / 2) });
      const best = samples.reduce((a, b) => (b.rtt < a.rtt ? b : a)); useRoom.setState({ offset: best.off, rtt: Math.round(best.rtt) }); break;
    }
    case 'room': useRoom.setState({ room: m.room }); break;
    case 'chat': useRoom.setState({ chat: [...st.chat, m].slice(-100) }); break;
    case 'chatlog': useRoom.setState({ chat: m.items }); break;
    case 'round': beginRound(m); break;
    case 'fill': market().remoteResult(m.oid, m.ok ? { ok: true, lamports: m.lamports, raw: m.raw } : { ok: false, reason: m.reason }); if (!m.ok) toast('Rejected by room: ' + m.reason, 'err'); break;
    case 'peers': for (const f of m.fills) if (f.pid !== st.pid) market().peerFill(f.tokenId, f.pid, f.name, f.side, f.amount); break;
    case 'checkpoint': for (const t of m.tokens) market().checkpoint(t.id, { vSol: t.vSol, vTok: t.vTok }); break;
    case 'board': useRoom.setState({ board: m.rows }); break;
    case 'deployed': toast(m.ok ? (m.winner ? 'Room: your deploy landed first' : `Room: ${m.reason}`) : `Room: ${m.reason}`, m.winner ? 'ok' : 'info'); break;
    case 'results': {
      useRoom.setState({ results: m });
      const L = useGame.getState().live;
      if (L?.mp) { const mine = m.standings.find((s) => s.pid === st.pid); endRound('mp'); const r = useGame.getState().result; if (r && mine) useGame.setState({ result: { ...r, ok: !!mine.ok, verdict: mine.ok ? 'HIT' : 'MISS', reason: `server: ${mine.reason}`, reactionMs: mine.reactionMs, details: { ...r.details, serverReceiptMs: mine.receivedMs, roomPoints: mine.points, validated: 'server' } } }); }
      break;
    }
    case 'error': toast(`Room error: ${m.message}`, 'err'); break;
    case 'kicked': closing = true; toast('Removed from room: ' + m.reason, 'err'); leave(true); break;
  }
}

function beginRound(m: Extract<ServerMsg, { t: 'round' }>) {
  const spec = m.spec as RoundSpec; const startAt = m.startAt;
  const clock = () => SIM_BASE + (serverNow() - startAt);
  // late join / reconnect: deterministic replay from the spec, then server checkpoints correct shared state
  const mk = new Market({ seed: spec.seed, background: true, now: 0, latencyMs: spec.netLatencyMs ?? 120 });
  mk.balance = Math.round(m.balanceSol * 1e9);
  mk.scheduleEvents(spec.id, SIM_BASE, spec.events);
  const ids = new Set(spec.events.flatMap((e) => (e.kind === 'launch' || e.kind === 'vampBot' ? [e.token.id] : [])));
  mk.remote = { tokens: ids, submit: (o) => send({ t: 'order', oid: o.id, side: o.side, tokenId: o.tokenId, lamports: o.lamports, raw: o.raw, inputMs: null }) };
  abortRound();
  mk.advance(Math.max(0, clock()));
  useMpMarket(mk, clock);
  applyBuyRules(useRoom.getState().room?.config ?? null);
  useRoom.setState({ spectator: m.spectator, round: m.round, startAt, results: null });
  if (!m.spectator) startRound({ spec, startSim: SIM_BASE, mp: true });
}

/** Report the claimed input reaction once we know it (sent at the end of our action window). */
export function reportResult() {
  const L = useGame.getState().live; if (!L?.mp) return;
  const r = (L as unknown as { _reported?: boolean }); if (r._reported) return; r._reported = true;
  // judge locally for the claim only; server re-judges correctness
  import('./game').then(({ judge }) => { const j = judge(L, 'mp'); send({ t: 'result', reactionMs: j.reactionMs, reaction2Ms: j.reaction2Ms ?? null, ok: j.ok, reason: j.reason.slice(0, 200), pnlSol: j.pnlSol }); });
}

// room buy rules temporarily override local presets; backup survives reload/crash and is restored on exit or boot
export function applyBuyRules(c: RoomConfig | null) {
  if (!c?.buyRules) return;
  const s = useSettings.getState();
  if (!load('roomBackup', null)) save('roomBackup', { quickBuySol: s.quickBuySol, buyPresets: s.buyPresets });
  s.set({ quickBuySol: c.buyRules.quickBuySol, buyPresets: [c.buyRules.quickBuySol] });
}
export function restoreBuyRules() {
  const b = load<{ quickBuySol: number; buyPresets: number[] } | null>('roomBackup', null);
  if (b) { useSettings.getState().set(b); remove('roomBackup'); }
}

export function leave(restore = true) {
  closing = true; ws?.close(1000); ws = null; if (syncTimer) clearInterval(syncTimer);
  const L = useGame.getState().live; if (L?.mp) abortRound();
  exitMp(); if (restore) restoreBuyRules();
  useRoom.setState({ code: null, status: 'idle', room: null, results: null, board: [], spectator: false });
}
export { serverNow };
